import React from 'react';
import { useOptimizedImage } from '@app/hooks/useOptimizedImage';
import Rating from './rating/rating';
import classes from './classes.module.scss';
import ReadingSinceAndCountItem, { readingSinceAndCountItemThemes } from '../advisor/nested_views/readingSinceAndCountItem';
import AdvisorVideoPlayer from '../advisor_video_player';
import ExpandedText from '../../../common/helpers/expanded_text';
import { isPrerenderMode } from '../../config/util';
import { isKA } from '../../../common/config/utils';
import { SvgIcon } from '../../../common/sprites';

const defaultAvatar = require('../header/img/home_placeholder.png');

const onPlayVideoBtnContainerClick = (e) => {
  e.preventDefault();
};

const getClassByEnv = (className) => {
  if (isKA()) {
    return `${ className }KA`;
  }
  return className;
};

const renderPlayVideoBtnContainer = (profileVideoUrl, id, analytics) => (
  <div className={ classes[getClassByEnv('playVideoBtnContainerHolder')] } suppressHydrationWarning>
    <div
      className={ classes[getClassByEnv('playVideoBtnContainer')] }
      onClick={ onPlayVideoBtnContainerClick }
      onKeyDown={ onPlayVideoBtnContainerClick }
      suppressHydrationWarning
    >
      <AdvisorVideoPlayer
        profileVideoUrl={ profileVideoUrl }
        needTrackProfileVideoEvent
        advisorId={ id }
        playImg = { (
          <SvgIcon className={ classes[getClassByEnv('playVideoImg')] } id="home_play" />
        ) }
        analytics={ analytics }
      />
    </div>
  </div>
);

const renderRateAndFaforite = ({
  loading, rating, isFavorite, isNewAdvisor, profileVideoUrl, id, analytics
}) => {
  if (loading) return null;
  return (
    <div className={ classes.rateAndFavouriteContainer }>
      <Rating rating={ rating } isNewAdvisor={ isNewAdvisor } />
      <div className={ classes.favouriteContainer } suppressHydrationWarning>
        <SvgIcon className={ classes.favoriteImg } id={ isFavorite && window.currentUserId ? 'fav_fill' : 'fav_empty' } />
      </div>
      {renderPlayVideoBtnContainer(profileVideoUrl, id, analytics)}
    </div>
  );
};

const renderNameSection = ({
  loading, nickname, serviceTitle, readingsCount, yearJoined, theme, countryFlagUrl = null, isNewAdvisor, showReadings = true
}) => {
  if (loading) return null;
  return (
    <div className = { classes.nickNameAndServiseTitleConatiner } suppressHydrationWarning>
      { countryFlagUrl && <img src={ countryFlagUrl } className={ classes.advisorCountryContainer } alt="country flag" /> }
      <div className={ classes.advisorNickNameContainer } suppressHydrationWarning>
        <ExpandedText
          customClasses={ { text: classes.nickNameText } }
          ellipsis
        >
          { nickname }
        </ExpandedText>
      </div>
      <div className={ classes.serviceTitleContainer } suppressHydrationWarning>
        <ExpandedText
          customClasses={ { text: classes.serviceTitleText } }
          ellipsis
        >
          { serviceTitle }
        </ExpandedText>
      </div>
      { showReadings && (
        <div className={ classes.readingSinceAndCountItemContainer } suppressHydrationWarning>
          <ReadingSinceAndCountItem
            readingsCount={ readingsCount }
            yearJoined={ yearJoined }
            isNewAdvisor={ isNewAdvisor }
            theme={ theme === 'advisors_from_country' ? readingSinceAndCountItemThemes.onHomePageAdvisorFromCountryAvatar : readingSinceAndCountItemThemes.onHomePageAdvisorAvatar }
            loading={ loading }
          />
        </div>
      ) }
    </div>
  );
};

const getAdvisorStatusClassName = ({ loading, status }) => {
  if (isPrerenderMode() || loading || !status) return classes.indicatorSkeleton;
  return classes[`${ status }Indicator`];
};

function AdvisorAvatar({
  profilePictureUrl,
  nickname,
  serviceTitle,
  status,
  isFavorite,
  rating,
  theme = null,
  loading = false,
  isNewAdvisor,
  readingsCount,
  yearJoined,
  profileVideoUrl,
  id,
  analytics = null,
  profilePictureDetails = {},
  t,
  desktopSize = [250, 250],
  countryFlagUrl = null,
  showReadings = true
}) {
  const renderAdvisorStatus = () => (
    <div className={ classes.indicatorContainer }>
      <div className={ getAdvisorStatusClassName({ loading, status }) } suppressHydrationWarning>
        <span className={ classes.statusLabel } suppressHydrationWarning>
          { t(`advisor_profile.status_label_${ status }`).toUpperCase() }
        </span>
      </div>
    </div>
  );

  // Use the optimized image hook with caching
  const optimizedUrl = useOptimizedImage({
    imageUrl: profilePictureUrl,
    imageDetails: profilePictureDetails,
    desktopSize,
    enableCaching: true // Enable image caching
  });

  // Get the final image URL with fallback
  const finalImageUrl = () => {
    if (loading) {
      return defaultAvatar;
    }
    return optimizedUrl || profilePictureUrl || defaultAvatar;
  };

  const renderAvatar = () => (
    <div className={ classes.mainContainer }>
      <div className={ classes.imgContainerHolder }>
        <div className={ classes.imgContainer }>
          <img
            src={ finalImageUrl() }
            className={ classes.image }
            alt={ nickname }
            style={ !profilePictureUrl ? { objectFit: 'scale-down' } : null }
            suppressHydrationWarning
          />
          { renderRateAndFaforite({
            loading, rating, isFavorite, isNewAdvisor, profileVideoUrl, id, analytics
          }) }
          { renderNameSection({
            loading, nickname, serviceTitle, readingsCount, yearJoined, theme, countryFlagUrl, isNewAdvisor, showReadings
          }) }
        </div>
      </div>
      <div className={ classes.statusContainer }>
        { renderAdvisorStatus() }
      </div>
    </div>
  );

  return (
    <div className={ classes[`${ theme }`] }>
      { renderAvatar() }
    </div>
  );
}

export default AdvisorAvatar;
